"use client";

import {
  Box,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  Stack,
  useTheme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import * as React from "react";
import { useTranslations } from "next-intl";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy, comparator) {
  return order === "desc"
    ? (a, b) => comparator(a, b, orderBy)
    : (a, b) => -comparator(a, b, orderBy);
}

function EnhancedTableToolbar(props) {
  const { numSelected } = props;

  const handleChangeFilter = (e) => {
    props.setFilter(e.target.value);
  };
  const t = useTranslations();
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        },
      ]}
    >
      <Select 
        onChange={handleChangeFilter} 
        value={props.filter}
        size="small"
        sx={{ maxWidth: '100%' }}
      >
        {Object.entries(props.filters).map(([label, val]) => (
          <MenuItem key={val} value={val}>
            {t(label)}
          </MenuItem>
        ))}
      </Select>
    </Toolbar>
  );
}

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, columns, visibleColumns } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };
  const t = useTranslations();
  return (
    <TableHead>
      <TableRow>
        {columns
          .filter(column => visibleColumns.includes(column.id))
          .map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.label)}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Card-based view for mobile displays
function MobileCardView({ data, columns }) {
  const t = useTranslations();
  
  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      {data.map((row) => (
        <Card key={row.id} sx={{ width: '100%' }}>
          <CardContent>
            {columns.map(({ id, label, Cell }) => (
              <Box key={id} sx={{ display: 'flex', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', minWidth: '40%' }}>
                  {t(label)}:
                </Typography>
                <Typography variant="body2">
                  {Cell ? <Cell row={row} /> : row[id]}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

/**
 * data is array of api data. (row data)
 * columns is array of column fields & how to display each cell, each element in column has properties:
 *** {
 ***   Cell?: Component accept row prop that has row data ,
 ***   id: column id, or field accessor from api data, if Cell is not provided, this will be used as key to get data value to display in cell,
 ***   label: label is header label,
 ***   priority?: number (1-3) - lower numbers have higher priority and remain visible on smaller screens
 *** }
 *** where fields marked as ? are optional, e.g. Cell.
 * dataFilterFn: filter function to filter data rows in table based on a filter, this function receives row data element as arg
 * initialSortOrder: initialSortOrder can be either "desc" or "asc"
 * sortingComparator: logic for determining sorting logic, could be different for dates for example, receives
 *** a, b, orderBy (where a, b are row datas to compare, and orderBy is column id by which rows are getting sorted)
 * initialOrderBy: initial column id by which rows are sorted
 * filter: selected filter (should be some value of a column)
 * setFilter: change or select a new filter function
 * filters: all possible (selectable) filters, is an object with key, value pairs
 * ** key: To display filter name is select options
 * ** value: corresponding filter values (should be some value of a column)
 * mobileBreakpoint: screen width breakpoint to switch to mobile view (default: 'sm')
 */
const DataTable = ({
  data = [],
  dataFilterFn = () => true,
  initialSortOrder = "asc",
  columns = [],
  sortingComparator = descendingComparator,
  initialOrderBy = columns[0]?.id,
  filter,
  setFilter,
  filters,
  mobileBreakpoint = 'sm'
}) => {
  const [order, setOrder] = React.useState(initialSortOrder);
  const [orderBy, setOrderBy] = React.useState(initialOrderBy);
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  
  const columnsWithPriority = React.useMemo(() => {
    return columns.map((col, index) => ({
      ...col,
      priority: col.priority || (index < 2 ? 1 : index < 4 ? 2 : 3)
    }));
  }, [columns]);

  // Determine which columns to show based on screen size
  const visibleColumns = React.useMemo(() => {
    if (isMobile) {
      // On mobile, only show highest priority columns
      return columnsWithPriority
        .filter(col => col.priority === 1)
        .map(col => col.id);
    } else {
      return columnsWithPriority.map(col => col.id);
    }
  }, [columnsWithPriority, isMobile]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = data.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredData = React.useMemo(
    () => [...data].filter(dataFilterFn),
    [data, dataFilterFn]
  );

  const visibleRows = React.useMemo(
    () =>
      filteredData
        .sort(getComparator(order, orderBy, sortingComparator))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, order, orderBy, page, rowsPerPage, sortingComparator]
  );

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  const t = useTranslations();
  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, marginBottom: 0 }}>
        <EnhancedTableToolbar
          filters={filters}
          filter={filter}
          setFilter={setFilter}
          numSelected={selected.length}
        />
        
        {isMobile ? (
          <Box 
            sx={{ 
              p: 2,
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            <MobileCardView 
              data={visibleRows} 
              columns={columnsWithPriority} 
            />
          </Box>
        ) : (
          <TableContainer 
            component={Paper}
            sx={{
              overflowX: 'auto', 
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,.2)',
                borderRadius: '4px',
              }
            }}
          >
            <Table
              sx={{ minWidth: 500 }}
              aria-labelledby="tableTitle"
              size={isMobile ? "small" : "medium"}
              stickyHeader
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
                columns={columnsWithPriority}
                visibleColumns={visibleColumns}
              />
              <TableBody>
                {visibleRows.map((row) => {
                  const isItemSelected = selected.includes(row.id);

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row.id)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer" }}
                    >
                      {columnsWithPriority
                        .filter(column => visibleColumns.includes(column.id))
                        .map(({ Cell, id }) => (
                        <TableCell key={id} align={"left"}>
                          {Cell ? <Cell row={row} /> : row[id]}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (isMobile ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={visibleColumns.length} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        <TablePagination
          rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '.MuiTablePagination-selectLabel': {
              fontSize: isMobile ? '0.75rem' : 'inherit'
            },
            '.MuiTablePagination-select': {
              fontSize: isMobile ? '0.75rem' : 'inherit'
            },
            '.MuiTablePagination-displayedRows': {
              fontSize: isMobile ? '0.75rem' : 'inherit'
            },
            '.MuiTablePagination-actions': {
              marginLeft: isMobile ? 0 : 2
            },
            overflowX: 'auto',
            width: '100%'
          }}
          labelRowsPerPage={isMobile ? t("Rows") + ":" : t("Rows per page")}
        />
      </Paper>
    </Box>
  );
};

export default DataTable;